import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router.js';
import { AuthProvider } from './auth/AuthContext.js';
import './index.css';

/**
 * LumoHR web app entry.
 *
 * Provider order:
 *   1. QueryClientProvider — wraps everything that uses useQuery/useMutation
 *   2. AuthProvider — children may call both; AuthProvider sits inside
 *      QueryClientProvider so hook ordering stays sane
 *   3. RouterProvider — last; route components consume auth + queries
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Payroll data changes slowly; 30s stale is a reasonable default.
      // Individual queries override via their own `staleTime`.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Don't retry 4xx — the server is telling us something we can't fix.
        if (error instanceof Error && 'status' in error) {
          const s = (error as { status?: number }).status;
          if (typeof s === 'number' && s >= 400 && s < 500) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
